import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Button, DrawerStepper } from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import {
  useValidationErrors,
  onSuccessMessage,
  onErrorMessage,
  deepCopy,
} from '@/helpers'

import {
  useAddressList,
  useCreateAddress,
  useUpdateProfile,
  QUERIES_ADMIN,
  useCreateProfile,
  useProfileDetail,
} from '@/services'

import { AddressProps, ProfileProps } from '@/types'

import { AddressList } from '../address-list'
import { AddressForm, shemaAddressItem } from '../address-form'
import { AddressDrawerProps } from './address-drawer.type'

export const AddressDrawer = ({
  isOpen,
  onHandleOpen,
  profileUuid,
}: AddressDrawerProps) => {
  const { t } = useTranslation('screenProfileList')

  const isNewRegister = !profileUuid

  const queryClient = useQueryClient()

  const [data, setData] = useState<Partial<ProfileProps>>({})

  const { data: dataProfile } = useProfileDetail(profileUuid || '')

  const [addressItem, setAddressItem] = useState<Partial<AddressProps>>({})

  const {
    isValid: isValidAddressItem,
    errors: addressItemErrors,
    onHandleListenErrors: onHandleListenErrorsAddressItem,
  } = useValidationErrors(shemaAddressItem(t), addressItem)

  const { data: dataAddresses } = useAddressList(data?.uuid || '')

  const onHandleNewAddress = ({
    onNavigateToNextStep,
  }: {
    onNavigateToNextStep: (route: string) => void
  }) => {
    onNavigateToNextStep('addressForm')
  }

  const { mutateAsync } = useCreateAddress(data?.uuid || '')

  const onSubmitNewAddress = async ({
    onNavigateToPreviousStep,
  }: {
    onNavigateToPreviousStep: () => void
  }) => {
    if (isValidAddressItem) {
      const response = await mutateAsync(addressItem)

      if (response.success) {
        onHandleListenErrorsAddressItem(false)
        onNavigateToPreviousStep()

        setAddressItem(response?.data || {})

        queryClient.invalidateQueries({
          queryKey: [QUERIES_ADMIN.ADDRESS.module, data?.uuid],
        })
      }
    } else {
      onHandleListenErrorsAddressItem(true)
    }
  }

  const { mutateAsync: mutateAsyncUpdateProfile } = useUpdateProfile()
  const { mutateAsync: mutateAsyncCreateProfile } = useCreateProfile()

  const onSubmitCompleted = async () => {
    let response: {
      success: boolean
      message: string
      data?: ProfileProps
    } = {
      success: false,
      message: '',
      data: {} as ProfileProps,
    }

    const payload = {
      ...data,
      churnedAt: data?.churnedAt ? data?.churnedAt : null,
      addressProof: undefined,
    }

    if (isNewRegister) {
      response = await mutateAsyncCreateProfile(payload as ProfileProps)
    } else {
      response = await mutateAsyncUpdateProfile(payload as ProfileProps)
    }

    if (response.success) {
      if (isNewRegister) {
        onSuccessMessage(
          t('profileFormSuccessCreateTitle'),
          t('profileFormSuccessCreateMessage'),
        )

        onHandleOpen(false)
      } else {
        onSuccessMessage(
          t('profileFormSuccessUpdateTitle'),
          t('profileFormSuccessUpdateMessage'),
        )
      }

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.PROFILE.module],
      })

      setData({})
      onHandleOpen(false)
    } else {
      if (isNewRegister) {
        onErrorMessage(
          t('profileFormErrorCreateTitle'),
          t('profileFormErrorCreateMessage'),
        )
      } else {
        onErrorMessage(
          t('profileFormErrorUpdateTitle'),
          t('profileFormErrorUpdateMessage'),
        )
      }
    }
  }

  useEffect(() => {
    if (isOpen && !isNewRegister && dataProfile?.data) {
      const deepDataCopy = deepCopy(dataProfile.data)

      if (deepDataCopy && deepDataCopy.taxSituation) {
        delete (deepDataCopy as { taxSituation?: string }).taxSituation
      }

      setData({
        ...deepDataCopy,
        keyAccountManagerId: dataProfile?.data?.keyAccountManager?.id
          ? `${dataProfile?.data?.keyAccountManager?.id}`
          : undefined,
        customerLevelSlug: dataProfile?.data?.customerLevel?.slug
          ? dataProfile?.data?.customerLevel?.slug
          : undefined,
        taxStatus: dataProfile?.data?.taxStatus || 'pending',
      })
    }

    if (!isOpen || isNewRegister) {
      setData({})
    }
  }, [isOpen, dataProfile?.data, isNewRegister])

  return (
    <DrawerStepper
      translations={{
        optional: t('profileFormOptional'),
      }}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      title={t('profileFormAddressListTitle')}
      initialStep="root"
      steps={{
        root: {
          isOptional: false,
          backNavigate: 'root',
          component: ({ onNavigateToNextStep }) => (
            <AddressList
              onClickNewAddress={() =>
                onHandleNewAddress({ onNavigateToNextStep })
              }
              addresses={dataAddresses?.data?.addresses || []}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <>
              <Button variant="secondary" onClick={onNavigateToPreviousStep}>
                {t('profileFormNavigateButtonBack')}
              </Button>

              <Button onClick={() => onSubmitCompleted()}>
                {t('profileFormNavigateButtonFinish')}
              </Button>
            </>
          ),
        },
        addressForm: {
          title: t('profileFormAddressFormTitle'),
          isOptional: false,
          backNavigate: 'root',
          component: (
            <AddressForm
              onChangeValues={setAddressItem}
              values={addressItem}
              errors={addressItemErrors as Record<string, string>}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <>
              <Button variant="secondary" onClick={onNavigateToPreviousStep}>
                {t('profileFormNavigateButtonBack')}
              </Button>

              <Button
                onClick={() => onSubmitNewAddress({ onNavigateToPreviousStep })}
              >
                {t('profileFormNavigateButtonFinish')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
